import { booleanAttribute, ChangeDetectionStrategy, Component, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { DsMessageModule } from '@bmw-ds/components';
import { NGXLogger } from 'ngx-logger';
import { delay, of, switchMap, tap } from 'rxjs';

import { AuthorizationWrapper } from '../../authorization/authorization.wrapper';
import { useI18n } from '../../i18n/services/i18n.service';
import { BackButtonProviderComponent } from '../../navigation/components/back-button/back-button-provider.component';
import { TopSectionComponent } from '../../navigation/components/top-section/top-section.component';
import { CloseCustomerEvent, OpenVehicleEvent } from '../../web-component/event.model';
import { WebComponentDirective } from '../../web-component/web-component.directive';
import { ProfilePage } from './profile.page';

@Component({
  selector: 'cdf-profile-web-component',
  imports: [AuthorizationWrapper, ProfilePage, BackButtonProviderComponent, DsMessageModule, TopSectionComponent],
  template: `
    <cdf-back-button-provider
      [label]="showBackButton() ? i18n('backButton.generic') : undefined"
      (back)="closeProfile.emit({ type: 'CLOSE_CUSTOMER' })"
    >
      <cdf-authorization-wrapper>
        @if (accountNumber(); as accountNumber) {
          <cdf-profile-page
            [accountNumber]="accountNumber"
            [enableVehicleSelection]="enableVehicleSelection()"
            (selectVehicle)="selectVehicle.emit({ type: 'OPEN_VEHICLE', vin: $event })"
          />
        } @else if (showNoAccountNumberWarning()) {
          <cdf-top-section>
            <ds-message tone="critical" [messageText]="i18n('profile.noAccountNumberWarning')" />
          </cdf-top-section>
        }
      </cdf-authorization-wrapper>
    </cdf-back-button-provider>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileWebComponent extends WebComponentDirective {
  protected readonly i18n = useI18n();
  private readonly logger = inject(NGXLogger);

  readonly accountNumber = input<string>();

  readonly showBackButton = input(false, { transform: booleanAttribute });

  readonly enableVehicleSelection = input(false, { transform: booleanAttribute });

  readonly closeProfile = output<CloseCustomerEvent>();

  readonly selectVehicle = output<OpenVehicleEvent>();

  /**
   * Indicates whether to show a warning message about missing account number.
   *
   * This signal becomes true if the account number is not provided,
   * after a delay of 500 milliseconds to avoid flickering as the component loads.
   */
  protected readonly showNoAccountNumberWarning = toSignal(
    toObservable(this.accountNumber).pipe(
      switchMap((accountNumber) => {
        if (accountNumber) {
          // Immediately hide the warning if account number is provided
          return of(false);
        } else {
          // Show the warning and log after a delay if account number is missing
          return of(true).pipe(
            delay(500),
            tap(() =>
              this.logger.error(
                '#CDFTF0229 ProfileWebComponent was initialised without an account number (after a timeout of 500 ms)',
              ),
            ),
          );
        }
      }),
    ),
    { initialValue: false },
  );
}
